import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../Auth/Auth';
import { Navigate } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { PresentationChartLineIcon, ClockIcon, UserGroupIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', current: false },
  { name: 'Operational Analytics', to: '/analytics/operations', current: true }
];

const OperationalAnalytics = () => {
  const { role, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Operational metrics
  const [totalJobs, setTotalJobs] = useState(0);
  const [avgJobDuration, setAvgJobDuration] = useState(0);
  const [jobsByStatus, setJobsByStatus] = useState([]);
  const [technicianPerformance, setTechnicianPerformance] = useState([]);
  const [monthlyJobTrends, setMonthlyJobTrends] = useState([]);
  const [completionRate, setCompletionRate] = useState(0);

  useEffect(() => {
    if (role === 'Service Advisor' || role === 'Admin') {
      fetchOperationalData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedYear]);

  const fetchOperationalData = async () => {
    setIsLoading(true);
    try {
      // Fetch jobs and technicians data
      const [jobsSnapshot, techniciansSnapshot] = await Promise.all([
        getDocs(collection(db, 'jobs')),
        getDocs(collection(db, 'technicians'))
      ]);

      // Build technician lookup map
      const techniciansById = {};
      techniciansSnapshot.forEach(doc => {
        const data = doc.data();
        techniciansById[doc.id] = data.technicianName || 'Unknown';
      });

      // Process jobs
      const statusMap = {};
      const monthlyData = Array(12).fill(null).map((_, idx) => ({
        month: idx,
        opened: 0,
        closed: 0,
        total: 0
      }));
      const technicianJobCount = {};
      let totalDuration = 0;
      let jobsWithDuration = 0;
      let completedJobs = 0;
      let totalJobsCount = 0;

      jobsSnapshot.forEach(doc => {
        const job = doc.data();
        const createdAt = job.createdAt?.toDate?.();

        if (createdAt && createdAt.getFullYear() === selectedYear) {
          totalJobsCount += 1;

          // Track monthly job trends
          const month = createdAt.getMonth();
          monthlyData[month].opened += 1;
          monthlyData[month].total += 1;

          if (job.status === 'Closed') {
            monthlyData[month].closed += 1;
            completedJobs += 1;
          }

          // Job status breakdown
          const status = job.status || 'Unknown';
          if (!statusMap[status]) {
            statusMap[status] = 0;
          }
          statusMap[status] += 1;

          // Technician performance
          const technicianId = job.technicianId;
          const technicianName = technicianId && techniciansById[technicianId]
            ? techniciansById[technicianId]
            : job.technician || 'Unassigned';

          if (!technicianJobCount[technicianName]) {
            technicianJobCount[technicianName] = {
              total: 0,
              completed: 0,
              inProgress: 0
            };
          }
          technicianJobCount[technicianName].total += 1;

          if (job.status === 'Closed') {
            technicianJobCount[technicianName].completed += 1;
          } else {
            technicianJobCount[technicianName].inProgress += 1;
          }

          // Calculate job duration
          if (job.tracker && Array.isArray(job.tracker) && job.tracker.length >= 2) {
            const startEntry = job.tracker[0];
            const endEntry = job.tracker[job.tracker.length - 1];

            if (startEntry.Date && endEntry.Date) {
              const startDate = startEntry.Date.toDate ? startEntry.Date.toDate() : new Date(startEntry.Date);
              const endDate = endEntry.Date.toDate ? endEntry.Date.toDate() : new Date(endEntry.Date);

              const durationHours = (endDate - startDate) / (1000 * 60 * 60);
              if (durationHours > 0 && durationHours < 720) { // Exclude unrealistic durations (> 30 days)
                totalDuration += durationHours;
                jobsWithDuration += 1;
              }
            }
          }
        }
      });

      // Calculate metrics
      const avgDuration = jobsWithDuration > 0 ? totalDuration / jobsWithDuration : 0;
      const completionPct = totalJobsCount > 0 ? (completedJobs / totalJobsCount) * 100 : 0;

      setTotalJobs(totalJobsCount);
      setAvgJobDuration(avgDuration);
      setCompletionRate(completionPct);

      // Job status breakdown
      const statusData = Object.entries(statusMap)
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);
      setJobsByStatus(statusData);

      // Top technician performance
      const techPerformance = Object.entries(technicianJobCount)
        .map(([name, data]) => ({
          name,
          ...data,
          completionRate: data.total > 0 ? ((data.completed / data.total) * 100) : 0
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
      setTechnicianPerformance(techPerformance);

      // Monthly trends
      setMonthlyJobTrends(monthlyData);

    } catch (error) {
      logger.error('Error fetching operational data:', error);
      toast.error('Failed to load operational analytics');
    } finally {
      setIsLoading(false);
    }
  };

  // Chart configurations
  const jobStatusChartData = {
    labels: jobsByStatus.map(s => s.status),
    datasets: [{
      data: jobsByStatus.map(s => s.count),
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',   // Green for Closed
        'rgba(251, 191, 36, 0.8)',   // Yellow for In Progress
        'rgba(59, 130, 246, 0.8)',   // Blue for Open
        'rgba(239, 68, 68, 0.8)',    // Red for other statuses
        'rgba(139, 92, 246, 0.8)',   // Purple
        'rgba(236, 72, 153, 0.8)'    // Pink
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const jobStatusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { padding: 10, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    }
  };

  const technicianChartData = {
    labels: technicianPerformance.map(t =>
      t.name.length > 15 ? t.name.substring(0, 15) + '...' : t.name
    ),
    datasets: [
      {
        label: 'Completed',
        data: technicianPerformance.map(t => t.completed),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1
      },
      {
        label: 'In Progress',
        data: technicianPerformance.map(t => t.inProgress),
        backgroundColor: 'rgba(251, 191, 36, 0.8)',
        borderColor: 'rgb(251, 191, 36)',
        borderWidth: 1
      }
    ]
  };

  const technicianChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const techData = technicianPerformance[context.dataIndex];
            return `Total Jobs: ${techData.total}\nCompletion Rate: ${techData.completionRate.toFixed(1)}%`;
          }
        }
      }
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      }
    }
  };

  const monthlyTrendsChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Jobs Opened',
        data: monthlyJobTrends.map(m => m.opened),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Jobs Closed',
        data: monthlyJobTrends.map(m => m.closed),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const monthlyTrendsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, usePointStyle: true }
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      }
    }
  };

  const formatDuration = (hours) => {
    if (hours < 24) {
      return `${hours.toFixed(1)} hrs`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = (hours % 24).toFixed(0);
      return `${days}d ${remainingHours}h`;
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-lime-50">
        <div className="text-center">
          <MoonLoader size={60} color="#84CC16" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-lime-50 to-green-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                <PresentationChartLineIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Operational Analytics</h1>
                <p className="text-sm text-gray-600 mt-1">Job performance, technician productivity, and workflow efficiency</p>
              </div>
            </div>

            {/* Year Selector */}
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl shadow-md">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-transparent text-sm font-medium"
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <MoonLoader size={50} color="#84CC16" />
              <p className="mt-4 text-sm font-medium text-gray-600">Loading operational data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Jobs */}
              <div className="bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-lime-100 text-sm font-medium">Total Jobs</p>
                    <h3 className="text-3xl font-bold mt-1">{totalJobs}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <PresentationChartLineIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-lime-100 text-xs">in {selectedYear}</p>
              </div>

              {/* Completion Rate */}
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Completion Rate</p>
                    <h3 className="text-3xl font-bold mt-1">{completionRate.toFixed(1)}%</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <CheckCircleIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-emerald-100 text-xs">Jobs completed successfully</p>
              </div>

              {/* Average Job Duration */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Avg Job Duration</p>
                    <h3 className="text-3xl font-bold mt-1">{formatDuration(avgJobDuration)}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ClockIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-orange-100 text-xs">From start to completion</p>
              </div>

              {/* Active Technicians */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Active Technicians</p>
                    <h3 className="text-3xl font-bold mt-1">{technicianPerformance.length}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <UserGroupIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-blue-100 text-xs">Working on jobs</p>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Job Status Breakdown */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Job Status Distribution</h3>
                  <p className="text-sm text-gray-600 mt-1">Current status of all jobs in {selectedYear}</p>
                </div>
                <div style={{ height: '350px' }}>
                  {jobsByStatus.length > 0 ? (
                    <Doughnut data={jobStatusChartData} options={jobStatusChartOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No job status data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Technician Performance */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Top 10 Technician Performance</h3>
                  <p className="text-sm text-gray-600 mt-1">Jobs completed and in progress by technician</p>
                </div>
                <div style={{ height: '350px' }}>
                  {technicianPerformance.length > 0 ? (
                    <Bar data={technicianChartData} options={technicianChartOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No technician data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Monthly Trends */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900">Monthly Job Trends</h3>
                <p className="text-sm text-gray-600 mt-1">Jobs opened vs closed throughout {selectedYear}</p>
              </div>
              <div style={{ height: '350px' }}>
                <Line data={monthlyTrendsChartData} options={monthlyTrendsChartOptions} />
              </div>
            </div>

            {/* Technician Details Table */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-lime-600 to-green-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">Technician Performance Details</h3>
                <p className="text-lime-100 text-sm mt-1">Comprehensive breakdown of technician activity</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Technician</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Jobs</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">In Progress</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {technicianPerformance.length > 0 ? (
                      technicianPerformance.map((tech, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{tech.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {tech.total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span className="px-2 py-1 text-xs font-bold rounded-full bg-green-100 text-green-700">
                              {tech.completed}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span className="px-2 py-1 text-xs font-bold rounded-full bg-yellow-100 text-yellow-700">
                              {tech.inProgress}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span className={`px-2 py-1 text-xs font-bold rounded ${
                              tech.completionRate >= 80 ? 'bg-green-100 text-green-700' :
                              tech.completionRate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {tech.completionRate.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <UserGroupIcon className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm">No technician data available for {selectedYear}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OperationalAnalytics;
